import { LinearFilter, Mesh, MeshBasicMaterial, Object3D, Texture } from 'three'
import { isLightweightViewer } from '../../viewport/readCapabilities'

const EXTRA_MAP_KEYS = [
  'normalMap',
  'roughnessMap',
  'metalnessMap',
  'aoMap',
  'emissiveMap',
  'bumpMap',
  'displacementMap',
] as const

function cheapenTexture(texture: Texture) {
  texture.anisotropy = 1
  texture.generateMipmaps = false
  texture.minFilter = LinearFilter
  texture.magFilter = LinearFilter
}

/** Cheap GPU flags only. Never copies pixels. */
export function prepareLightweightTexture(texture: Texture | null | undefined): Texture | null | undefined {
  if (!texture || !isLightweightViewer()) return texture
  if (texture.userData?.__lightweightPrepared) return texture
  cheapenTexture(texture)
  texture.needsUpdate = true
  texture.userData.__lightweightPrepared = true
  return texture
}

/**
 * Catalog GLB only (muebles). Keep albedo, drop PBR maps, use unlit materials.
 * Does not touch walls/CSG. Safe to run once after parse on compact.
 */
export function lightenUnitGlbForMobile(root: Object3D | null | undefined) {
  if (!root || !isLightweightViewer()) return
  const scene = (root as Object3D & { scene?: Object3D }).scene ?? root
  if (!scene || typeof scene.traverse !== 'function') return
  if (scene.userData?.__unitLightened) return
  scene.userData.__unitLightened = true

  scene.traverse((obj) => {
    const mesh = obj as Mesh
    if (!mesh.isMesh || !mesh.material) return
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
    const next = materials.map((mat) => {
      if (!mat) return mat
      const map = 'map' in mat ? (mat as { map?: Texture | null }).map : null
      if (map?.isTexture) prepareLightweightTexture(map)
      for (const key of EXTRA_MAP_KEYS) {
        const tex = (mat as unknown as Record<string, Texture | null | undefined>)[key]
        if (tex && tex.isTexture) {
          ;(mat as unknown as Record<string, Texture | null>)[key] = null
        }
      }
      if (mat.type === 'MeshBasicMaterial') return mat
      const basic = new MeshBasicMaterial({
        name: mat.name,
        color: 'color' in mat && (mat as { color?: { clone?: () => unknown } }).color?.clone
          ? (mat as { color: { clone: () => unknown } }).color.clone()
          : '#ffffff',
        map: map ?? null,
        opacity: 'opacity' in mat ? ((mat as { opacity?: number }).opacity ?? 1) : 1,
        transparent:
          'opacity' in mat ? ((mat as { opacity?: number }).opacity ?? 1) < 1 : Boolean(mat.transparent),
        side: 'side' in mat ? (mat as { side: number }).side : undefined,
      })
      mat.dispose()
      return basic
    })
    mesh.material = Array.isArray(mesh.material) ? next : next[0]
  })
}
